import { useMemo } from 'react';
import { TowTruckIcon } from '@rsos/assets/icons';
import useHasAccessToTowPro from '@rsos/hooks/useHasAccessToTowPro';
import {
  useGetTowProRequestsQuery,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import TowProListItem from '../TowPro/TowProListItem';
import TowProView from '../TowPro/TowProView';
import {
  getTowProLatestActivityMs,
  getTowProQueueCategory,
} from '../TowPro/towProStatusUtils';
import type {
  ResourceRequestAdapter,
  ResourceRequestIngestionMetadata,
  ResourceRequestListItemProps,
  ResourceRequestPickupLocation,
} from './types';

function useTowProResourceList({ skip }: { skip: boolean }): {
  requests: TowProRequestResponse[];
  isFetching: boolean;
} {
  const { data, isFetching } = useGetTowProRequestsQuery(undefined, {
    skip,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const requests = useMemo(() => data ?? [], [data]);
  return { requests, isFetching };
}

function getTowProPickupLocation(
  request: TowProRequestResponse,
): ResourceRequestPickupLocation | null {
  const { latitude, longitude } = request.request.data.addressInput.location;
  return { latitude, longitude };
}

/**
 * Tow requests can only be created from Unite; the digital dispatch API has
 * no ingestion-source field to report.
 */
function getTowProIngestionMetadata(): ResourceRequestIngestionMetadata {
  return { ingestionSource: 'unite', requestMethod: null };
}

function TowProQueueListItem({
  request,
  isUnread,
  onClick,
}: ResourceRequestListItemProps<TowProRequestResponse>): JSX.Element {
  return (
    <TowProListItem request={request} isUnread={isUnread} onClick={onClick} />
  );
}

export const towProAdapter: ResourceRequestAdapter<TowProRequestResponse> = {
  id: 'towpro',
  title: 'Tow',
  source: 'TowPro',
  description:
    'Request a tow truck dispatched through the local rotation providers',
  Icon: TowTruckIcon,
  useEnabled: useHasAccessToTowPro,
  useList: useTowProResourceList,
  getId: request => String(request.id),
  getCallerId: request => request.callerId,
  getPickupLocation: getTowProPickupLocation,
  getIngestionMetadata: getTowProIngestionMetadata,
  getQueueInfo: (request, nowMs) => ({
    category: getTowProQueueCategory(request, nowMs),
    activityMs: getTowProLatestActivityMs(request),
  }),
  ListItemComponent: TowProQueueListItem,
  ViewComponent: TowProView,
};
